import { type FC, useCallback, useContext, useMemo, useState } from "react";
import { generatePath, useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";
import { Button } from "@snapchat/snap-design-system";

import { AbsoluteRoute, type RouteParams } from "../../../core/routes";
import { PageContent, PageContentSpinner } from "../../../layout/components";
import { UserInfoContext } from "../../../shared/api/contexts";
import { Flexbox } from "../../../shared/components";
import { useDefaultOrganizationAndLensSpaceNames } from "../../../shared/hooks";
import { type KeyedOption } from "../../../shared/models";
import { TextLink } from "../../../shared/styles";
import {
    type GetAllPresubmissionInfoWithOrgsAvailableForLensSpaceCreationQuery,
    type GetAllPresubmissionInfoWithOrgsAvailableForLensSpaceCreationQueryVariables,
    getAllPresubmissionInfoWithOrgsAvailableForLensSpaceCreation,
} from "../../api/queries";
import { useCreateLensSpace } from "../../hooks";
import { isInvalidTextInput } from "../../validation";

import { SelectOrganizationDropdownInputSection } from "./DropdownInputSections";
import { LensSubmissionFlowPageHeader } from "./LensSubmissionFlowPageHeader";
import { LensSpaceTextInputSection } from "./TextInputSections";
import {
    CreateLensFolder,
    CreateLensFolderHeader,
    CreateLensFolderSubHeader,
    CreateNewOrganizationButtonText,
} from "./messages.generated";
import { buttonLinkContainer, createLensSpaceButton, pageContent } from "./shared.styles";
import { createTextInputHandler } from "./shared.util";
import { sortOrganizationsAvailableForLensSpaceCreation } from "./sortOrganizationsAvailableForLensSpaceCreation";

interface CreateLensSpacePage {
    organizationOptions: KeyedOption[];
    defaultLensSpaceName?: string;
}

const CreateLensSpacePage: FC<CreateLensSpacePage> = ({ organizationOptions, defaultLensSpaceName = "" }) => {
    const { submissionId = "" } = useParams<RouteParams[AbsoluteRoute.SUBMIT_LENS_FOLDER_CREATE]>();
    const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
    const [lensSpaceName, setLensSpaceName] = useState(defaultLensSpaceName);
    const {
        createLensSpace,
        loading: creatingLensSpace,
        error,
    } = useCreateLensSpace(selectedOrganizationId, lensSpaceName);

    const isLensSpaceNameInvalid = isInvalidTextInput(lensSpaceName);
    const isCreateLensFolderButtonDisabled =
        creatingLensSpace || selectedOrganizationId.length === 0 || isLensSpaceNameInvalid;
    const handleLensSpaceNameChange = useMemo(() => createTextInputHandler(setLensSpaceName), [setLensSpaceName]);

    const handleCreate = useCallback(() => void createLensSpace(), [createLensSpace]);

    const createOrganizationAndLensFolderUrl = generatePath(AbsoluteRoute.CREATE_ORG_LENS_FOLDER, {
        routeTo: "submit",
        submissionId,
    });

    if (error) {
        throw error;
    }

    return (
        <PageContent>
            <LensSubmissionFlowPageHeader
                headerText={<CreateLensFolderHeader />}
                subheaderText={<CreateLensFolderSubHeader />}
            />
            <Flexbox flexDirection="column" className={pageContent}>
                <div>
                    <SelectOrganizationDropdownInputSection
                        onChange={setSelectedOrganizationId}
                        isLoading={creatingLensSpace}
                        options={organizationOptions}
                    />
                    <div className={buttonLinkContainer}>
                        <TextLink href={createOrganizationAndLensFolderUrl}>
                            <CreateNewOrganizationButtonText />
                        </TextLink>
                    </div>
                </div>
                <LensSpaceTextInputSection
                    inputValue={lensSpaceName}
                    onChange={handleLensSpaceNameChange}
                    isLoading={creatingLensSpace}
                />
                <Flexbox flexDirection="row-reverse">
                    <Button
                        type="primary"
                        className={createLensSpaceButton}
                        isDisabled={isCreateLensFolderButtonDisabled}
                        onClick={handleCreate}
                        isPending={creatingLensSpace}
                    >
                        <CreateLensFolder />
                    </Button>
                </Flexbox>
            </Flexbox>
        </PageContent>
    );
};

export const CreateLensSpacePageContainer: FC = () => {
    const { submissionId = "" } = useParams<RouteParams[AbsoluteRoute.SUBMIT_LENS_FOLDER_CREATE]>();
    const { loading: loadingAllPresubmissionInfo, data } = useQuery<
        GetAllPresubmissionInfoWithOrgsAvailableForLensSpaceCreationQuery,
        GetAllPresubmissionInfoWithOrgsAvailableForLensSpaceCreationQueryVariables
    >(getAllPresubmissionInfoWithOrgsAvailableForLensSpaceCreation, {
        variables: { submissionId },
    });

    const { loading: loadingDefaultNames, defaultLensSpaceName } = useDefaultOrganizationAndLensSpaceNames();

    const userInfo = useContext(UserInfoContext);

    const organizationOptions = useMemo(() => {
        if (!data) return [];
        const sortedOrganizations = sortOrganizationsAvailableForLensSpaceCreation(
            data.getMyLensesOrganizationsAvailableForLensSpaceCreation.organizationsList,
            userInfo?.data?.getUserProfile.defaultOrganizationId ?? "",
        );
        return sortedOrganizations.map((organization) => ({
            key: organization.id,
            value: organization.id,
            label: organization.name,
        }));
    }, [data, userInfo]);

    if (loadingAllPresubmissionInfo || loadingDefaultNames || userInfo?.loading) {
        return <PageContentSpinner />;
    }

    return (
        <CreateLensSpacePage defaultLensSpaceName={defaultLensSpaceName} organizationOptions={organizationOptions} />
    );
};
